import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  ArrowRight, X, PenTool, UserPlus, Store, Sparkles,
  CheckCircle2, ChevronLeft, ChevronRight, BookOpen, Megaphone
} from 'lucide-react';

const STEPS = [
  {
    id: 'signature',
    title: 'Create Your Email Signature',
    desc: 'Pick a style and generate your professional email signature — this is where ads will appear.',
    icon: PenTool,
    page: '/signatures',
    cta: 'Create Signature',
  },
  {
    id: 'invite',
    title: 'Invite Pros to Join',
    desc: 'Invite professionals to join ConnectMail and advertise on your email signature so you can start earning!',
    icon: UserPlus,
    page: '/signatures',
    cta: 'Invite Pros',
    skippable: true,
    skipLabel: 'Skip for now',
  },
  {
    id: 'library',
    title: 'Choose a Signature',
    desc: 'Select one of your created signatures to place in the marketplace where advertisers can find you.',
    icon: Store,
    page: '/signatures',
    query: 'blink=choose',
    cta: 'Go to Library',
  },
  {
    id: 'marketplace',
    title: 'Place in Marketplace',
    desc: 'Choose a slot in the marketplace to advertise on. If your account is not activated, you\'ll be prompted to activate first.',
    icon: Megaphone,
    page: '/marketplace',
    cta: 'Go to Marketplace',
  },
];

export const OnboardingTour = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(-1);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(false);
  const [tabExpanded, setTabExpanded] = useState(false);

  useEffect(() => {
    if (!user) return;
    const completed = localStorage.getItem('cm_tour_completed') === 'true';
    const savedStep = parseInt(localStorage.getItem('cm_tour_step') || '-1');
    if (completed) {
      setTourCompleted(true);
    } else if (savedStep >= 0) {
      setStep(savedStep);
    } else {
      setShowWelcome(true);
    }
  }, [user]);

  const goToStep = (n) => {
    setStep(n);
    localStorage.setItem('cm_tour_step', String(n));
  };

  const handleStart = () => {
    setShowWelcome(false);
    goToStep(0);
    navigate(STEPS[0].page);
  };

  const handleSkipAll = () => {
    setShowWelcome(false);
    localStorage.setItem('cm_tour_completed', 'true');
    setStep(-1);
    setTourCompleted(true);
  };

  const handleNext = () => {
    const next = step + 1;
    if (next >= STEPS.length) {
      localStorage.setItem('cm_tour_completed', 'true');
      setStep(-1);
      setShowComplete(true);
      setTourCompleted(true);
    } else {
      goToStep(next);
      navigateToStep(next);
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      goToStep(step - 1);
      navigateToStep(step - 1);
    }
  };

  const navigateToStep = useCallback((n) => {
    const target = STEPS[n];
    let url = target.page;
    if (target.query) url += '?' + target.query;
    const [path] = url.split('?');
    if (location.pathname !== path) navigate(url);
  }, [location.pathname, navigate]);

  const handleGoTo = () => {
    if (step < 0) return;
    navigateToStep(step);
  };

  const handleFinish = () => {
    localStorage.setItem('cm_tour_completed', 'true');
    setStep(-1);
    setTourCompleted(true);
  };

  const handleReopenTour = () => {
    localStorage.removeItem('cm_tour_completed');
    localStorage.removeItem('cm_tour_step');
    setTourCompleted(false);
    setTabExpanded(false);
    setShowWelcome(true);
  };

  if (!user) return null;
  if (location.pathname.startsWith('/admin')) return null;

  // Completion celebration
  if (showComplete) {
    return (
      <div className="fixed bottom-6 right-6 z-[55] animate-in slide-in-from-bottom-3 fade-in duration-300" data-testid="tour-complete">
        <div className="bg-background/95 backdrop-blur-md border border-border rounded-xl shadow-xl w-80 p-5 text-center">
          <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
          <h3 className="text-sm font-bold">You're All Set!</h3>
          <p className="text-xs text-muted-foreground mt-1">Your signature is ready. Start earning by inviting pros to advertise!</p>
          <Button size="sm" onClick={() => setShowComplete(false)} className="mt-3 w-full text-xs gap-1" data-testid="tour-done-btn">
            Got it <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      </div>
    );
  }

  // Welcome modal
  if (showWelcome) {
    return (
      <div className="fixed bottom-6 right-6 z-[55] animate-in slide-in-from-bottom-3 fade-in duration-300" data-testid="tour-welcome">
        <div className="bg-background/95 backdrop-blur-md border border-border rounded-xl shadow-xl w-80 overflow-hidden">
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <h3 className="text-sm font-bold">Quick Setup Guide</h3>
              </div>
              <button onClick={handleSkipAll} className="text-muted-foreground hover:text-foreground p-1" data-testid="tour-skip-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Let's get you earning! Create your email signature, invite pros to advertise on it, and place it in the marketplace.
            </p>
            <div className="flex items-center justify-center gap-4 py-1">
              {STEPS.map((s, i) => (
                <div key={s.id} className="flex items-center gap-1">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <s.icon className="w-3 h-3 text-primary" />
                  </div>
                  {i < STEPS.length - 1 && <div className="w-3 h-px bg-muted-foreground/30" />}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handleSkipAll} className="flex-1 text-xs h-8">Skip</Button>
              <Button size="sm" onClick={handleStart} className="flex-1 text-xs gap-1 h-8" data-testid="tour-start-btn">
                Let's Go <ArrowRight className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Collapsed tab for completed tour
  if (tourCompleted) {
    return (
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 z-[55]" data-testid="tour-tab-wrapper">
        {tabExpanded && (
          <div className="mb-0 bg-background/95 backdrop-blur-md border border-border border-b-0 rounded-t-xl shadow-xl w-72 animate-in slide-in-from-bottom-2 duration-200" data-testid="tour-tab-expanded">
            <div className="p-3 space-y-1.5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Setup Guide</span>
                <button onClick={() => setTabExpanded(false)} className="p-0.5 hover:bg-muted rounded">
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </div>
              <div className="max-h-56 overflow-y-auto space-y-1">
                {STEPS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => {
                      let url = s.page;
                      if (s.query) url += '?' + s.query;
                      navigate(url);
                      setTabExpanded(false);
                    }}
                    className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg hover:bg-muted/50 transition-colors text-left"
                  >
                    <s.icon className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                    <span className="text-[11px] truncate">{s.title}</span>
                  </button>
                ))}
              </div>
              <button onClick={handleReopenTour} className="w-full text-[11px] text-primary hover:underline text-center py-1" data-testid="tour-restart-btn">
                Restart walkthrough
              </button>
            </div>
          </div>
        )}
        <button
          onClick={() => setTabExpanded(v => !v)}
          className="flex items-center gap-1.5 bg-background/90 backdrop-blur-sm border border-border border-b-0 text-foreground text-xs font-medium px-3 py-1.5 rounded-t-lg shadow-md transition-colors hover:bg-muted"
          data-testid="tour-collapsed-tab"
        >
          <BookOpen className="w-3 h-3" />
          Setup Guide
        </button>
      </div>
    );
  }

  // Active tour step
  if (step < 0 || step >= STEPS.length) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  return (
    <div
      className="fixed bottom-6 right-6 z-[55] animate-in slide-in-from-bottom-3 fade-in duration-300"
      data-testid={`tour-step-${current.id}`}
    >
      <div className="bg-background/90 backdrop-blur-lg border border-border/60 rounded-xl shadow-xl w-[300px] overflow-hidden">
        {/* Progress bar */}
        <div className="flex items-center gap-0.5 px-3 pt-2">
          {STEPS.map((_, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1 rounded-full transition-all ${
                i < step ? 'bg-green-500' : i === step ? 'bg-primary' : 'bg-muted'
              }`} />
            </div>
          ))}
        </div>
        <div className="px-3 pt-1 text-right">
          <span className="text-[9px] text-muted-foreground">Step {step + 1} of {STEPS.length}</span>
        </div>

        <div className="px-3 pb-3 space-y-2">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Icon className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xs font-bold leading-tight">{current.title}</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{current.desc}</p>
            </div>
            <button onClick={handleFinish} className="text-muted-foreground hover:text-foreground p-0.5 flex-shrink-0" data-testid="tour-close-btn">
              <X className="w-3 h-3" />
            </button>
          </div>

          <div className="flex items-center gap-1">
            {!isFirst && (
              <Button variant="ghost" size="sm" onClick={handlePrev} className="h-6 px-1.5 text-[11px]" data-testid="tour-prev-btn">
                <ChevronLeft className="w-3 h-3" />
              </Button>
            )}

            {current.skippable && (
              <button onClick={handleNext} className="text-[10px] text-muted-foreground hover:text-foreground">
                {current.skipLabel || 'Skip'}
              </button>
            )}

            <div className="flex-1" />

            <Button variant="ghost" size="sm" onClick={handleGoTo} className="h-6 text-[11px] px-2" data-testid="tour-goto-btn">
              {current.cta}
            </Button>
            <Button
              size="sm"
              onClick={handleNext}
              className="h-6 text-[11px] px-2.5 gap-1 ring-2 ring-primary/30 ring-offset-1 animate-pulse"
              data-testid="tour-next-btn"
            >
              {isLast ? 'Finish' : 'Next'} <ArrowRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
